import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardMedia,
  Button,
  IconButton,
  TextField,
  Collapse,
  ButtonBase,
  Chip,
  Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CloudUpload,
  Delete,
  OpenInNew,
  BurstMode,
  PhotoLibrary,
  Crop,
  Visibility,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  SwapVert as SwapVertIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as api from '../../services/api';
import PurgeOldGalleries from '../shared/PurgeOldGalleries';
import { useI18n } from '../../i18n/useI18n';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { isStaffUser } from '../../utils/staffUser';
import MPhotoSectionTabs from '../shared/MPhotoSectionTabs';
import PhotosSegmentedControl from '../shared/PhotosSegmentedControl';
import DeviceFilterSelect from '../shared/DeviceFilterSelect';
import GalleryStatsPanel from '../shared/GalleryStatsPanel';
import UserPageShell, { userCardEnterSx } from '../shared/UserPageShell';
import { filterMachinesByAppProduct } from '../../utils/deviceMgmtFeatures';
import { rewriteToCdn } from '../../utils/cdn';

type MonoTab = 'sub-photos' | 'frames' | 'photos' | 'stats';

const PAGE_SIZE = 10;
const MONO_ACCENT = '#0f3460';
/** Cùng hồng navbar */
const MONO_WARM = '#ff8ba7';
const MONO_PINK_DEEP = '#e86a8a';

const dateFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    bgcolor: alpha(MONO_WARM, 0.04),
    '& fieldset': { borderColor: alpha(MONO_WARM, 0.16) },
    '&:hover fieldset': { borderColor: alpha(MONO_WARM, 0.35) },
    '&.Mui-focused fieldset': {
      borderColor: MONO_PINK_DEEP,
      borderWidth: 1.5,
    },
    '& input': {
      py: 1.15,
      fontSize: '0.875rem',
      color: MONO_ACCENT,
    },
  },
} as const;

const MPhotoMono: React.FC = () => {
  const { pages: t, formatMessage } = useI18n();
  const { user } = useSelector((state: RootState) => state.auth);
  const readOnly = isStaffUser(user);
  const [tab, setTab] = useState<MonoTab>('photos');
  const viewTab = readOnly ? 'photos' : tab;
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [subPhotos, setSubPhotos] = useState<api.MonoAsset[]>([]);
  const [frames, setFrames] = useState<api.MonoAsset[]>([]);

  const [monoPhotoGroups, setMonoPhotoGroups] = useState<api.MonoPhotoGallery[]>([]);
  const [photosPage, setPhotosPage] = useState(1);
  const [photosHasMore, setPhotosHasMore] = useState(true);
  const [photosLoadingMore, setPhotosLoadingMore] = useState(false);
  const [photosTotal, setPhotosTotal] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [machineCodeFilter, setMachineCodeFilter] = useState('');
  const [userMachines, setUserMachines] = useState<api.MachineInfo[]>([]);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const monoMachines = React.useMemo(
    () => filterMachinesByAppProduct(userMachines, 'mono'),
    [userMachines]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getUserMachines();
        if (!cancelled) setUserMachines(res.machines || []);
      } catch (error) {
        console.error('Failed to load machines for mono photo filter:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasDateFilter = Boolean(startDate || endDate);
  const showDatePanel = isFilterExpanded;

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setIsFilterExpanded(false);
  };

  const convertToISO = (datetimeLocal: string): string => {
    if (!datetimeLocal) return '';
    return new Date(datetimeLocal).toISOString();
  };

  const buildPhotoFilter = useCallback((): api.MonoPhotoGroupFilter => {
    const filter: api.MonoPhotoGroupFilter = { sortOrder };
    if (startDate) filter.startDate = convertToISO(startDate);
    if (endDate) filter.endDate = convertToISO(endDate);
    if (machineCodeFilter) filter.machineCode = machineCodeFilter;
    return filter;
  }, [startDate, endDate, sortOrder, machineCodeFilter]);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const [subPhotoData, frameData] = await Promise.all([
        api.getMonoSubPhotos(),
        api.getMonoFrames(),
      ]);
      setSubPhotos(subPhotoData || []);
      setFrames(frameData || []);
    } catch (error) {
      console.error('Error loading mono assets:', error);
      toast.error('Không thể tải dữ liệu M-Photo Mono');
    } finally {
      setLoading(false);
    }
  };

  const fetchPhotosFirstPage = useCallback(async () => {
    try {
      setLoading(true);
      setMonoPhotoGroups([]);
      setPhotosPage(1);
      setPhotosHasMore(true);
      const filter = buildPhotoFilter();
      const result = await api.getMonoPhotoGroups(1, PAGE_SIZE, filter);
      const data = result?.data || [];
      const total = result?.pagination?.total ?? 0;
      setMonoPhotoGroups(data);
      setPhotosTotal(total);
      setPhotosHasMore(data.length < total);
      setPhotosPage(2);
    } catch {
      toast.error('Không thể tải danh sách ảnh Mono');
    } finally {
      setLoading(false);
    }
  }, [buildPhotoFilter]);

  const fetchPhotosNextPage = useCallback(async (page: number) => {
    if (photosLoadingMore) return;
    try {
      setPhotosLoadingMore(true);
      const filter = buildPhotoFilter();
      const result = await api.getMonoPhotoGroups(page, PAGE_SIZE, filter);
      const data = result?.data || [];
      const total = result?.pagination?.total ?? 0;
      setMonoPhotoGroups((prev) => {
        const next = [...prev, ...data];
        setPhotosHasMore(next.length < total);
        return next;
      });
      setPhotosTotal(total);
      setPhotosPage(page + 1);
    } catch {
      toast.error('Không thể tải thêm ảnh');
    } finally {
      setPhotosLoadingMore(false);
    }
  }, [photosLoadingMore, buildPhotoFilter]);

  useEffect(() => {
    if (tab === 'photos') {
      fetchPhotosFirstPage();
    } else {
      fetchAssets();
    }
  }, [tab, startDate, endDate, sortOrder, machineCodeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab !== 'photos') return;
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && photosHasMore && !photosLoadingMore) {
          fetchPhotosNextPage(photosPage);
        }
      },
      { rootMargin: '200px' }
    );

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => observerRef.current?.disconnect();
  }, [tab, photosHasMore, photosLoadingMore, photosPage, fetchPhotosNextPage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    disabled: tab === 'photos' || tab === 'stats',
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.webp'] },
    maxFiles: 10,
    onDrop: async (acceptedFiles) => {
      if (!acceptedFiles.length) return;
      try {
        setUploading(true);
        for (const file of acceptedFiles) {
          const formData = new FormData();
          if (tab === 'sub-photos') {
            formData.append('photo', file);
            await api.uploadMonoSubPhoto(formData);
          } else {
            formData.append('frame', file);
            await api.uploadMonoFrame(formData);
          }
        }
        toast.success('Upload thành công');
        await fetchAssets();
      } catch (error: any) {
        toast.error(error?.response?.data?.error || 'Upload thất bại');
      } finally {
        setUploading(false);
      }
    },
  });

  const handleDelete = async (id: string) => {
    try {
      if (tab === 'sub-photos') {
        await api.deleteMonoSubPhoto(id);
        setSubPhotos((prev) => prev.filter((item) => item._id !== id));
      } else if (tab === 'frames') {
        await api.deleteMonoFrame(id);
        setFrames((prev) => prev.filter((item) => item._id !== id));
      }
      toast.success('Đã xóa thành công');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Xóa thất bại');
    }
  };

  const currentItems = tab === 'sub-photos' ? subPhotos : frames;

  const handleDeletePhotoGroup = async (folderId: string) => {
    try {
      await api.deleteMonoPhotoGroup(folderId);
      setMonoPhotoGroups((prev) => prev.filter((g) => g.folderId !== folderId));
      setPhotosTotal((prev) => Math.max(0, prev - 1));
      toast.success('Đã xóa gallery ảnh Mono');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Xóa gallery thất bại');
    }
  };

  return (
    <UserPageShell enter>
      <Typography
        variant="h5"
        component="h1"
        sx={{
          textAlign: 'center',
          fontWeight: 800,
          fontSize: { xs: '1.35rem', sm: '1.75rem' },
          lineHeight: 1.2,
          color: MONO_PINK_DEEP,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          mb: { xs: 1.5, md: 2.5 },
        }}
      >
        Mono
      </Typography>

      <MPhotoSectionTabs
        value={readOnly ? 'photos' : tab}
        onChange={setTab}
        accentColor={MONO_PINK_DEEP}
        size="sm"
        sticky
        stickyTop={{ xs: 56, sm: 64 }}
        options={
          readOnly
            ? [{ value: 'photos' as const, label: t.mPhotoMono.tabs.photos, icon: <PhotoLibrary fontSize="inherit" /> }]
            : [
          { value: 'photos', label: t.mPhotoMono.tabs.photos, icon: <PhotoLibrary fontSize="inherit" /> },
          { value: 'sub-photos', label: t.mPhotoMono.tabs.subPhotos, icon: <BurstMode fontSize="inherit" /> },
          { value: 'frames', label: t.mPhotoMono.tabs.frames, icon: <Crop fontSize="inherit" /> },
          { value: 'stats', label: t.mPhotoMono.tabs.stats, icon: <BarChartIcon fontSize="inherit" /> },
        ]
        }
      />

      {viewTab === 'photos' && (
        <PurgeOldGalleries
          purgeKey="user:mono"
          requestBatch={() => api.purgeExpiredMonoPhotoGroups()}
          onPurged={() => {
            void fetchPhotosFirstPage();
          }}
        />
      )}
      {viewTab === 'photos' && (
        <DeviceFilterSelect
          value={machineCodeFilter}
          onChange={setMachineCodeFilter}
          machines={monoMachines}
          accentColor={MONO_PINK_DEEP}
          sticky
          stickyTop={{ xs: 108, sm: 116 }}
        />
      )}

      {!readOnly && tab !== 'photos' && tab !== 'stats' ? (
        <Box
          {...getRootProps()}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 1.25, sm: 2 },
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 1.25, sm: 2 },
            mb: 2,
            borderRadius: 2.5,
            cursor: 'pointer',
            border: '1.5px solid',
            borderColor: isDragActive ? alpha(MONO_PINK_DEEP, 0.7) : alpha(MONO_PINK_DEEP, 0.45),
            background: isDragActive
              ? `linear-gradient(135deg, #fff 0%, ${alpha(MONO_WARM, 0.22)} 100%)`
              : `linear-gradient(135deg, #fff 0%, ${alpha(MONO_WARM, 0.14)} 100%)`,
            boxShadow: isDragActive
              ? `0 1px 2px rgba(15,23,42,0.08), 0 10px 28px ${alpha(MONO_PINK_DEEP, 0.22)}`
              : `0 1px 2px rgba(15,23,42,0.06), 0 8px 22px ${alpha(MONO_PINK_DEEP, 0.14)}`,
            transition: 'all 0.22s ease',
            '&:hover': {
              borderColor: alpha(MONO_PINK_DEEP, 0.65),
              boxShadow: `0 2px 4px rgba(15,23,42,0.08), 0 12px 28px ${alpha(MONO_PINK_DEEP, 0.2)}`,
              transform: 'translateY(-1px)',
            },
          }}
        >
          <input {...getInputProps()} />
          <Box
            sx={{
              flexShrink: 0,
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(MONO_PINK_DEEP, 0.16),
              color: MONO_PINK_DEEP,
              boxShadow: `inset 0 0 0 1px ${alpha(MONO_PINK_DEEP, 0.28)}`,
            }}
          >
            {uploading ? <CircularProgress size={20} sx={{ color: MONO_PINK_DEEP }} /> : <CloudUpload />}
          </Box>
          <Box sx={{ minWidth: 0, textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 700, color: MONO_PINK_DEEP, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              {isDragActive ? t.mPhotoMono.dropActive : t.photos.uploadCta}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                display: { xs: 'none', sm: 'block' },
                fontSize: '0.8125rem',
                color: alpha(MONO_PINK_DEEP, 0.7),
              }}
            >
              {t.mPhotoMono.dropIdle}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            mb: 2,
            p: { xs: 1.5, sm: 2 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: alpha(MONO_WARM, 0.16),
            background: `linear-gradient(180deg, ${alpha('#fff', 0.98)} 0%, ${alpha(MONO_WARM, 0.06)} 100%)`,
            boxShadow: `0 4px 18px ${alpha(MONO_WARM, 0.06)}`,
          }}
        >
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: showDatePanel ? 1.25 : 0, rowGap: 0.75 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <SwapVertIcon sx={{ fontSize: 18, color: alpha(MONO_PINK_DEEP, 0.75), display: { xs: 'none', sm: 'block' } }} />
              <PhotosSegmentedControl
                value={sortOrder}
                onChange={(v) => setSortOrder(v)}
                size="sm"
                accentColor={MONO_PINK_DEEP}
                options={[
                  { value: 'desc', label: t.common.newest },
                  { value: 'asc', label: t.common.oldest },
                ]}
              />
            </Box>

            <ButtonBase
              onClick={() => setIsFilterExpanded((prev) => !prev)}
              sx={{
                ml: { xs: 'auto', sm: 0 },
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.25,
                py: 0.7,
                borderRadius: 2,
                border: '1px solid',
                borderColor: showDatePanel || hasDateFilter
                  ? alpha(MONO_PINK_DEEP, 0.45)
                  : alpha(MONO_WARM, 0.4),
                bgcolor: showDatePanel || hasDateFilter
                  ? alpha(MONO_WARM, 0.28)
                  : alpha(MONO_WARM, 0.14),
                color: MONO_PINK_DEEP,
              }}
            >
              <FilterListIcon sx={{ fontSize: 16 }} />
              <Typography component="span" sx={{ fontWeight: 700, fontSize: { xs: '0.72rem', sm: '0.8rem' } }}>
                {t.photos.filterByDate}
              </Typography>
              {hasDateFilter && (
                <Chip
                  size="small"
                  label="ON"
                  sx={{
                    height: 16,
                    fontSize: '0.6rem',
                    fontWeight: 800,
                    bgcolor: MONO_PINK_DEEP,
                    color: '#fff',
                    '& .MuiChip-label': { px: 0.6 },
                  }}
                />
              )}
            </ButtonBase>

            {hasDateFilter && (
              <IconButton size="small" onClick={clearDateFilter} sx={{ color: MONO_PINK_DEEP, p: 0.5 }}>
                <ClearIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}

            {!loading && (
              <Typography
                variant="caption"
                sx={{
                  ml: { xs: 0, sm: 'auto' },
                  width: { xs: '100%', sm: 'auto' },
                  fontWeight: 600,
                  color: alpha(MONO_PINK_DEEP, 0.9),
                }}
              >
                {hasDateFilter
                  ? formatMessage(t.mPhotoMono.totalFiltered, { n: photosTotal })
                  : formatMessage(t.mPhotoMono.total, { n: photosTotal })}
              </Typography>
            )}
          </Stack>

          <Collapse in={showDatePanel} timeout={220}>
            <Box
              sx={{
                mt: 0.5,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.25,
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.5,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: alpha(MONO_PINK_DEEP, 0.9),
                    fontSize: '0.68rem',
                  }}
                >
                  {t.common.fromDate}
                </Typography>
                <TextField
                  fullWidth
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={dateFieldSx}
                />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.5,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: alpha(MONO_PINK_DEEP, 0.9),
                    fontSize: '0.68rem',
                  }}
                >
                  {t.common.toDate}
                </Typography>
                <TextField
                  fullWidth
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={dateFieldSx}
                />
              </Box>
            </Box>
          </Collapse>
        </Box>
      )}

      {tab !== 'photos' &&
        tab !== 'stats' &&
        (loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: { xs: 1.25, sm: 2 },
            }}
          >
            {currentItems.map((item, index) => (
              <Card
                key={item._id}
                elevation={0}
                sx={{
                  borderRadius: 2.5,
                  border: '1.5px solid',
                  borderColor: alpha(MONO_PINK_DEEP, 0.42),
                  bgcolor: '#fff',
                  overflow: 'hidden',
                  boxShadow: `0 1px 2px rgba(15,23,42,0.06), 0 8px 22px ${alpha(MONO_PINK_DEEP, 0.16)}`,
                  ...userCardEnterSx(index),
                }}
              >
                <CardMedia
                  component="img"
                  image={item.url}
                  alt={item.name}
                  sx={{ height: { xs: 140, sm: 200 }, objectFit: 'contain', bgcolor: alpha(MONO_WARM, 0.14) }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.75 }}>
                  <Button
                    size="small"
                    startIcon={<Visibility fontSize="small" />}
                    href={item.url}
                    target="_blank"
                    sx={{ textTransform: 'none', color: MONO_PINK_DEEP }}
                  >
                    {t.common.view}
                  </Button>
                  <IconButton size="small" color="error" onClick={() => handleDelete(item._id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Card>
            ))}
          </Box>
        ))}

      {viewTab === 'photos' && (
        <>
          {loading ? (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    sm: 'repeat(3, minmax(0, 1fr))',
                    md: 'repeat(4, minmax(0, 1fr))',
                  },
                  gap: { xs: 1.25, sm: 2 },
                }}
              >
                {monoPhotoGroups.map((group, index) => {
                  const coverRaw = group.photos?.[0]?.url || '';
                  const cover = rewriteToCdn(coverRaw);
                  const created = group.createdAt
                    ? new Date(group.createdAt).toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' })
                    : '';
                  return (
                    <Card
                      key={group.folderId}
                      elevation={0}
                      sx={{
                        width: '100%',
                        borderRadius: 2.5,
                        border: '1.5px solid',
                        borderColor: alpha(MONO_PINK_DEEP, 0.42),
                        bgcolor: '#fff',
                        overflow: 'hidden',
                        boxShadow: `0 1px 2px rgba(15,23,42,0.06), 0 8px 22px ${alpha(MONO_PINK_DEEP, 0.16)}`,
                        ...userCardEnterSx(index),
                        transition: 'box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease',
                        '&:hover': {
                          borderColor: alpha(MONO_PINK_DEEP, 0.65),
                          boxShadow: `0 2px 4px rgba(15,23,42,0.08), 0 12px 28px ${alpha(MONO_PINK_DEEP, 0.24)}`,
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Box sx={{ position: 'relative', bgcolor: alpha(MONO_WARM, 0.14) }}>
                        {cover ? (
                          <CardMedia
                            component="img"
                            image={cover}
                            alt={group.folderId}
                            loading="lazy"
                            sx={{
                              aspectRatio: '3 / 4',
                              objectFit: 'contain',
                              width: '100%',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              aspectRatio: '3 / 4',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <PhotoLibrary sx={{ fontSize: 40, color: alpha(MONO_PINK_DEEP, 0.45) }} />
                          </Box>
                        )}
                        <Chip
                          size="small"
                          label={formatMessage(t.mPhotoMono.photoCount, {
                            n: group.photos?.length || 0,
                            p: group.printCount || 0,
                          })}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            height: 22,
                            fontWeight: 800,
                            fontSize: '0.68rem',
                            bgcolor: alpha('#fff', 0.92),
                            color: MONO_PINK_DEEP,
                          }}
                        />
                      </Box>
                      <Box sx={{ px: 1.25, py: 1, bgcolor: alpha(MONO_WARM, 0.1) }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: MONO_PINK_DEEP,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: { xs: '0.78rem', sm: '0.875rem' },
                          }}
                          title={group.folderId}
                        >
                          {group.folderId}
                        </Typography>
                        {created && (
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', mb: 0.75, color: alpha(MONO_PINK_DEEP, 0.75), fontWeight: 500 }}
                          >
                            {created}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                          <Button
                            size="small"
                            startIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                            href={group.viewUrl}
                            target="_blank"
                            sx={{
                              minWidth: 0,
                              px: 1,
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              color: MONO_PINK_DEEP,
                              '&:hover': { bgcolor: alpha(MONO_WARM, 0.18) },
                            }}
                          >
                            {t.common.view}
                          </Button>
                          {!readOnly && (
                          <IconButton size="small" color="error" onClick={() => handleDeletePhotoGroup(group.folderId)}>
                            <Delete fontSize="small" />
                          </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Card>
                  );
                })}
              </Box>

              <Box ref={sentinelRef} sx={{ height: 40, mt: 2 }} />

              {photosLoadingMore && (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={28} />
                </Box>
              )}

              {!photosHasMore && monoPhotoGroups.length > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 1 }}>
                  {formatMessage(t.mPhotoMono.endOfList, {
                    shown: monoPhotoGroups.length,
                    total: photosTotal,
                  })}
                </Typography>
              )}

              {!loading && monoPhotoGroups.length === 0 && (
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                  {hasDateFilter ? t.mPhotoMono.emptyFiltered : t.mPhotoMono.empty}
                </Typography>
              )}
            </>
          )}
        </>
      )}

      {viewTab === 'stats' && <GalleryStatsPanel channel="mono" accentColor={MONO_PINK_DEEP} />}
    </UserPageShell>
  );
};

export default MPhotoMono;
